/**
 * 票链云 — 登录态与权限上下文
 *
 * 前端所有「菜单能不能看见、按钮能不能点」的判断都收敛到这里，
 * 依据是登录接口返回的 AuthContext.permissions（后端按角色展开的权限码集合），
 * 前端不自行拼接角色 → 权限的映射，避免与后端角色配置漂移。
 *
 * 注意：前端隐藏菜单只是体验优化，真正的拦截在后端接口层。
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  PrincipalType,
  RiskLevel,
  isRiskAllowed,
  type AuthContext,
  type LoginRequest,
  type LoginResponse,
} from '@ilc/shared';
import {
  authApi,
} from '@/api/endpoints';
import {
  clearTokens,
  getAccessToken,
  onUnauthorized,
  setTokens,
} from '@/api/client';

interface AuthState {
  auth: AuthContext | null;
  /** 是否已完成一次「恢复登录态」的尝试，避免刷新页面时菜单闪烁 */
  bootstrapped: boolean;
  loading: boolean;
  /** 登录/恢复失败的原因，登录页展示 */
  lastError: string | null;

  login: (req: LoginRequest) => Promise<AuthContext>;
  logout: (silent?: boolean) => Promise<void>;
  /** 刷新页面后用本地令牌调用 /auth/me 恢复权限上下文 */
  bootstrap: () => Promise<void>;

  /* —— 判断辅助 —— */
  isAuthenticated: () => boolean;
  isPlatform: () => boolean;
  /** 是否拥有某个功能权限码（平台运营端不走租户权限码，另行以 principalType 判断） */
  can: (code: string) => boolean;
  canAny: (codes: string[]) => boolean;
  /** 金额是否在本人审核上限内（null 表示不限） */
  canApproveAmount: (cents: number | null | undefined) => boolean;
  /** 风险等级是否在本人可放行范围内 */
  canReleaseRisk: (level: RiskLevel | null | undefined) => boolean;
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.replace(`/login?redirect=${redirect}`);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      auth: null,
      bootstrapped: false,
      loading: false,
      lastError: null,

      async login(req: LoginRequest): Promise<AuthContext> {
        set({ loading: true, lastError: null });
        try {
          const res: LoginResponse = await authApi.login(req);
          setTokens(res.accessToken, res.refreshToken);
          set({ auth: res.auth, loading: false, bootstrapped: true, lastError: null });
          return res.auth;
        } catch (err) {
          const message = err instanceof Error ? err.message : '登录失败，请稍后重试';
          set({ loading: false, lastError: message });
          throw err;
        }
      },

      async logout(silent = false): Promise<void> {
        try {
          if (getAccessToken()) await authApi.logout();
        } catch {
          // 登出接口失败不影响本地清理：JWT 无状态，丢弃令牌即视为登出
        }
        clearTokens();
        set({ auth: null, lastError: null, bootstrapped: true });
        if (!silent) redirectToLogin();
      },

      async bootstrap(): Promise<void> {
        const token = getAccessToken();
        if (!token) {
          set({ auth: null, bootstrapped: true });
          return;
        }
        set({ loading: true });
        try {
          const auth = await authApi.me();
          set({ auth, loading: false, bootstrapped: true });
        } catch {
          // 令牌失效或后端未就绪：清空登录态，由路由守卫决定是否跳登录页
          clearTokens();
          set({ auth: null, loading: false, bootstrapped: true });
        }
      },

      isAuthenticated(): boolean {
        return get().auth !== null;
      },

      isPlatform(): boolean {
        return get().auth?.principalType === PrincipalType.PLATFORM;
      },

      can(code: string): boolean {
        const auth = get().auth;
        if (!auth) return false;
        // 平台运营端不使用租户权限码，平台页面的可见性由 principalType 控制
        if (auth.principalType === PrincipalType.PLATFORM) return true;
        return auth.permissions.includes(code);
      },

      canAny(codes: string[]): boolean {
        return codes.some((code) => get().can(code));
      },

      canApproveAmount(cents: number | null | undefined): boolean {
        const limit = get().auth?.amountLimitCents;
        if (limit === null || limit === undefined) return true;
        if (cents === null || cents === undefined) return true;
        return cents <= limit;
      },

      canReleaseRisk(level: RiskLevel | null | undefined): boolean {
        const max = get().auth?.maxRiskLevel;
        if (!level) return true;
        return isRiskAllowed(level, max);
      },
    }),
    {
      name: 'ilc.auth',
      storage: createJSONStorage(() => window.localStorage),
      // 只持久化权限上下文，令牌由 api/client 单独管理，避免两处不一致
      partialize: (state) => ({ auth: state.auth, bootstrapped: state.bootstrapped }),
    },
  ),
);

/* 令牌失效时清理登录态并跳登录页（由 axios 拦截器触发） */
onUnauthorized(() => {
  clearTokens();
  useAuthStore.setState({ auth: null });
  redirectToLogin();
});
